import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  ActivatedRoute,
  Router,
  RouterOutlet,
} from '@angular/router';
import { Pagination } from '@core/models/pagination.model';
import { TransactionsService } from '@core/services/transactions.service';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { BehaviorSubject, Observable, Subscription, take } from 'rxjs';
import { ButtonDirective } from '@shared/directives/button.directive';
import {
  DropDownModel,
  DropdownComponent,
} from '@shared/ui/dropdown/dropdown.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DecimalPipe, TitleCasePipe } from '@angular/common';
import {
  Account,
  BeneficiaryDetailsData,
  TransactionsListDatum,
} from '@core/models/admin/account';
import { NotificationFacade } from '@core/facades/notification.facade';
import { dropdownMenuAnimation } from '@shared/animations/animations';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { StatusComponent } from '@shared/ui/status/status.component';
import { IFILTER } from 'app/app.constants';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { TableActions } from 'app/auth/table-store/table.actions';
import { Store } from '@ngxs/store';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';
import { AccountManagementService } from '@core/services/account-management.service';

@Component({
  selector: 'app-admin-transactions',
  standalone: true,
  providers: [DecimalPipe, TitleCasePipe],
  templateUrl: './wallet-transactions.component.html',
  styleUrl: './wallet-transactions.component.scss',
  animations: [dropdownMenuAnimation],
  imports: [
    CommonModule,
    ButtonDirective,
    DropdownComponent,
    ReactiveFormsModule,
    FormsModule,
    RouterOutlet,
    TableSkeletonLoaderComponent,
    StatusComponent,
    FiltersComponent,
    TableNavigatorComponent,
    PermissionDirective,
    TableRefreshComponent
  ],
})
export class WalletTransactionsComponent implements OnInit, OnDestroy {
  filterMenuState: 'visible' | 'hidden' = 'hidden';
  showAction = false;

  providusBalanceSubject$ = new BehaviorSubject<number>(0);
  providusBalance$ = this.providusBalanceSubject$.asObservable();

  accessBankBalanceSubject$ = new BehaviorSubject<number>(0);
  accessBankBalance$ = this.accessBankBalanceSubject$.asObservable();

  vertoBalanceSubject$ = new BehaviorSubject<number>(0);
  vertoBalance$ = this.vertoBalanceSubject$.asObservable();

  paystackBalanceSubject$ = new BehaviorSubject<number>(0);
  paystackBalance$ = this.paystackBalanceSubject$.asObservable();

  // approve
  isApproveDialogOpen: boolean = false;
  isApproveInProgress: boolean = false;
  approveDialogQuestion!: any;
  disableApproveAction: boolean = true;
  // reject
  isRejectDialogOpen: boolean = false;
  isRejectInProgress: boolean = false;

  isFetchingTransactions$!: Observable<boolean>;
  hadFilterBefore = false;

  isApproveLoadingText!: string;
  subs: Subscription[] = [];
  // transactionsList: any[] = [];
  transactionsList: TransactionsListDatum[] = [];
  filteredList: any[] = [];
  totalCount: number = 0;
  currentPage: number = 1;
  transaction: any;
  searchText = '';
  isLoading: boolean = false;
  isFilterActive: boolean = false;
  filterService: any;
  totalCredit = 0;
  totalDebit = 0;
  isFiltered: boolean = false;
  $index: any;
  actionList: DropDownModel[] = [];
  transactionId!: string | null;
  accountId!: string | null;
  transactionDetails!: TransactionsListDatum | null;
  beneficiaryDetails!: BeneficiaryDetailsData | null;
  isReverseDialogOpen: boolean = false;
  isReverseInProgress: boolean = false;
  transactionsListSubject = new BehaviorSubject<any>(null);
  transactionsList$ = this.transactionsListSubject.asObservable();
  totalCountSubject = new BehaviorSubject<number>(0);
  totalCount$: Observable<number> = this.totalCountSubject.asObservable();

  private breadCrumbService = inject(BreadCrumbService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private tranService = inject(TransactionsService);
  private change = inject(ChangeDetectorRef);
  private transactionService = inject(TransactionsService);
  private notify = inject(NotificationFacade);
  //filterService = inject(FilterService);
  private decimalPipe = inject(DecimalPipe);
  private titleCasePipe = inject(TitleCasePipe);
  private store = inject(Store);
  private confirmDialogService = inject(ConfirmDialogService);
  private permissionService = inject(RolesAndPermissionsService);
  private accountService = inject(AccountManagementService)

  filterStructure: IFILTER[] = [
    {
      filterKey: 'start_date',
      label: 'Start Date',
      type: 'date',
    },
    {
      filterKey: 'end_date',
      label: 'End Date',
      type: 'date',
    },{
      filterKey: 'channel',
      label: 'Channel',
      type: 'dropdown',
      dropdown: [
        { key: 'card', value: 'Card' },
        { key: 'transfer', value: 'Transfer' },
      ],
    },
    {
      filterKey: 'type',
      label: 'Transaction Type',
      type: 'dropdown',
      dropdown: [
        { key: 'credit', value: 'Credit' },
        { key: 'debit', value: 'Debit' },
      ],
    },
    {
      filterKey: 'status',
      label: 'Status',
      type: 'dropdown',
      dropdown: [
        { key: 'withdrawn', value: 'Withdrawn' },
        { key: 'withheld', value: 'Withheld' },
        { key: 'denied', value: 'Denied' },
        { key: 'completed', value: 'Completed' },
      ],
    },
  ];

  constructor() {
    this.transactionsList$ = this.store.select(TableSelectors.getTableData('walletTransactions'));
    this.totalCount$ = this.store.select(TableSelectors.getTableTotalCount('walletTransactions'));
    this.isFetchingTransactions$ = this.store.select(TableSelectors.getTableLoading('walletTransactions'));
    this.providusBalance$ = this.store.select(TableSelectors.getProvidusBankBalance());
    this.accessBankBalance$ = this.store.select(TableSelectors.getAccessBankBalance());
    this.vertoBalance$ = this.store.select(TableSelectors.getVertoBankBalance());
    this.paystackBalance$ = this.store.select(TableSelectors.getPaystackBankBalance());

  }

  ngOnInit() {
    this.breadCrumbService.setPageBreadCrumb(this.activatedRoute);

    this.loadWalletTransctionList();
    this.getProvidusSettlementAcctBalance();
    this.getAccessBankSettlementAcctBalance();
    this.getVertoSettlementAcctBalance();
    this.getPaystackSettlementAcctBalance();
    this.WalletFiltersEventListener();
  }

  WalletFiltersEventListener() {
    this.activatedRoute.queryParams.subscribe((params) => {
      let query = '';
      if (params['start_date']) {
        query = `start_date=${params['start_date']}&`;
      }
      if (params['end_date']) {
        query += `end_date=${params['end_date']}&`;
      }
      if (params['type']) {
        query += `type=${params['type']}&`;
      }
      if (params['channel']) {
        query += `channel=${params['channel']}&`;
      }
      if (params['status']) {
        query += `status=${params['status']}&`;
      }
      if (params['search']) {
        query += `search_text=${params['search']}&`;
      }
      if (query) {
        this.isFilterActive = true;
      }
      // Handle pagination
      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (query) {
        query += `skip=${skip}&take=${take}`;
        this.hadFilterBefore = true;
        this.store.dispatch(new TableActions.FilterWalletTransctionsList({ filter: query }));
      } else {
         // only re-fetch if there was a previous filter
         if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          this.store.dispatch(new TableActions.LoadTableData({
            tableKey: 'walletTransactions',
            params: new Pagination({ skip: skip, size: take }),
          }));
        }
      }
    });
  }

  loadWalletTransctionList(page = 1) {
    const sub = this.transactionsList$.pipe(take(1)).subscribe(transaction => {
      const hasData = Array.isArray(transaction) && transaction.length > 0;
      // only dispatch if store is empty
      if (!hasData) {
        this.store.dispatch(new TableActions.LoadTableData({
          tableKey: 'walletTransactions',
          params: new Pagination({ 
            skip: (page - 1) * 10, 
            size: 10 
          }),
        }));
      }
    });
    this.subs.push(sub);
  }

  initDropdownList(transaction: TransactionsListDatum) {
    return (this.actionList = [
      {
        title: 'Approve',
        action: () => {
          const transactionId = transaction.id;
          const accountId = transaction.account_id;

          this.router.navigateByUrl(
            `/admin/transactions/wallet/approve?tran_id=${transactionId}&account_id=${accountId}`
          );
        },
        show:
          this.permissionService.hasPermission(['process_withdrawal_request']) && (transaction.status === 'pending' || transaction.status === 'withheld'),
      },

      {
        title: 'Reject',
        action: () => {
          this.transactionId = transaction.id;
          this.accountId = transaction.account.id;
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Reject Transaction',
            description: `Are you sure you want to reject this transaction? This action cannot be undone`,
            loadingText: 'Rejecting Transaction, please wait...',
            bgConfirmButton: "danger",
            action: () => this.rejectTransaction(),
            onClose: () => {
              this.transactionId = null;
              this.accountId = null
            },
          });
        },
        show:
          this.permissionService.hasPermission(['process_withdrawal_request']) && (transaction.status === 'pending' || transaction.status === 'withheld'),
      },
      {
        action: () => {
          this.viewDetails(transaction);
        },
        title: 'View Details',
        show: true && this.permissionService.hasPermission(['get_transaction_details']),
      },
      {
        action: () => {
          this.viewAccountDetails(transaction);
        },
        title: 'View Account',
        show: true && this.permissionService.hasPermission(['get_individual']),
      },
      {
        action: () => {
          this.viewDetailsToReverse(transaction);
        },
        title: 'Reverse Fund',
        show: !transaction?.reversed_request_id && this.permissionService.hasPermission(['reverse_fund']),
      },
    ]);
  }

  viewDetailsToReverse(transaction: TransactionsListDatum) {
    this.router.navigate(
      [`/admin/transactions/wallet/details`, `${transaction.id}`],
      {
        relativeTo: this.activatedRoute,
        state: { transaction },
        queryParams: { action: 'reverseTransaction' },
      }
    );
  }

  viewAccountDetails(txn: any) {
    this.router.navigateByUrl(
      `/admin/accounts/${txn.account.account_type}/${txn.account_id}`
    );
  }
  viewDetails(transaction: TransactionsListDatum) {
    this.router.navigate(
      [`/admin/transactions/wallet/details`, `${transaction.id}`],
      {
        relativeTo: this.activatedRoute,
        state: { transaction },
      }
    );
  }

  rejectTransaction() {
    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    if (!(this.transactionId && this.accountId)) {
      this.isRejectInProgress = false;
      this.notify.error('Invalid transaction or account details');
      return;
    }
    const sub = this.transactionService
      .processDebitTransaction({
        status: 'denied',
        transactionId: this.transactionId,
        accountId: this.accountId,
      })
      .subscribe({
        next: (response) => {
          this.confirmDialogService.updateDialog({
            isDialogLoading: false,
            isOpen: false,
          });

          this.updateTransactionStatus({
            status: 'denied',
            transactionId: this.transactionId as string,
          });

          this.notify.success('Transaction rejected successfully');
          this.change.detectChanges();
        },
        error: (err) => {
          this.confirmDialogService.updateDialog({ isDialogLoading: false });

          this.change.detectChanges();
        },
      });

    this.subs.push(sub);
  }

  updateTransactionStatus(updatePaylod: {
    status: 'denied' | 'completed';
    transactionId: string;
  }) {
    let transactionIndex = 0;
    this.transactionsList.forEach((transaction, index) => {
      if (transaction.id === this.transactionId) {
        transactionIndex = index;
        return;
      }
    });

    if (transactionIndex === -1) {
      return;
    }

    let newStatus =
      updatePaylod.status === 'completed' ? 'completed' : 'denied';

    const updatedTransactions = {
      ...this.transactionsList[transactionIndex],
      status: newStatus,
    };

    const updatedTransactionList = [
      ...this.transactionsList.slice(0, transactionIndex),
      updatedTransactions,
      ...this.transactionsList.slice(transactionIndex + 1),
    ];

    this.transactionsList = updatedTransactionList;
  }

  getBeneficiaryDetails(accountId: string) {
    this.isApproveInProgress = true;
    this.isApproveLoadingText = 'Fetching Transaction Details, please wait...';

    const sub = this.transactionService
      .getBeneficiaryDetails(accountId)
      .subscribe({
        next: (response) => {
          this.beneficiaryDetails = response.data;
          if (response.data.bank_account_number === null) {
            this.disableApproveAction = true;
          }
          this.isApproveInProgress = false;

          let amount = this.transactionDetails?.transaction_amount
            ? this.decimalPipe.transform(
                (this.transactionDetails?.transaction_amount as number) / 100,
                '1.2-2'
              )
            : 'not provided';
          let bankAccountName = response.data.bank_account_name
            ? this.titleCasePipe.transform(response.data.bank_account_name)
            : 'not provided';

          this.approveDialogQuestion = `
          <p>Are you sure you want to approve this Debit transaction? This action cannot be undone. </p>

          <div class="flex flex-col text-sm gap-1 mt-1">
            <h5>Recipient Details</h5>
            <div class="flex justify-between">
              <div class="text-gray-500"> Account Name: </div>
              <div class="font-bold">${bankAccountName}</div>
            </div>
            <div class="flex justify-between">
              <div class="text-gray-500"> Account Number: </div>
              <div class="font-bold">${response.data.bank_account_number ? response.data.bank_account_number : 'not provided'}</div>
            </div>
            <div class="flex justify-between">
              <div class="text-gray-500"> Amount: </div>
              <div class="font-bold"> ${amount}</div>
            </div>
            <div class="flex justify-between">
              <div class="text-gray-500"> Remark: </div>
              <div class="font-bold">${this.transactionDetails?.tran_remarks ? this.transactionDetails?.tran_remarks : 'not provided'}</div>
            </div>
          </div>
          `;

          this.disableApproveAction = false;

          this.change.detectChanges();
        },
        error: (err) => {
          // console.log(err);
          this.isApproveInProgress = false;
          this.change.detectChanges();
        },
      });

    this.subs.push(sub);
  }

  getCountByTypeAndStatus(type: string, status: string) {
    const sub = this.tranService
      .getCountByTypeAndStatus(type, status)
      .subscribe({
        next: (resp: any) => {
          if (type === 'credit') {
            this.totalCredit = resp.data.total;
          } else if (type === 'debit') {
            this.totalDebit = resp.data.total;
          }
        },
        error: (err) => {},
      });
    this.subs.push(sub);
  }

  toggleFilterMenu() {
    this.filterMenuState =
      this.filterMenuState === 'hidden' ? 'visible' : 'hidden';
  }

  getTransactionsList(page: Pagination = new Pagination()) {
    const sub = this.tranService.viewWalletTransactionsList(page).subscribe({
      next: (res) => {
        this.transactionsList = res.data.data;
        this.totalCount = res.data.total_count;
      },
      error: (err) => {},
    });
    this.subs.push(sub);
  }


  navigate(currentPage: number) {

    if (this.isFilterActive) {
      this.router.navigate([], {
        queryParams: { page: currentPage },

        queryParamsHandling: 'merge',
      });
    }
  }

  getProvidusSettlementAcctBalance() {
    const sub = this.providusBalance$.pipe(take(1)).subscribe((balance) => {
      if (!balance) {
        this.store.dispatch(new TableActions.GetProvidusBalance())
      }
    })
    this.subs.push(sub);
  }

  getAccessBankSettlementAcctBalance() {
    const sub = this.accessBankBalance$.pipe(take(1)).subscribe((balance) => {
      if (!balance) {
        this.store.dispatch(new TableActions.GetAccessBankBalance())
      }
    })
    this.subs.push(sub);
  }

  getVertoSettlementAcctBalance() {
    const sub = this.vertoBalance$.pipe(take(1)).subscribe((balance) => {
      if (!balance) {
        this.store.dispatch(new TableActions.GetVertoBalance())
      }
    })
    this.subs.push(sub);
  }

  getPaystackSettlementAcctBalance() {
    const sub = this.paystackBalance$.pipe(take(1)).subscribe((balance) => {
      if (!balance) {
        this.store.dispatch(new TableActions.GetPaystackBalance())
      }
    })
    this.subs.push(sub);
  }

  get totalPage() {
    const remain = this.totalCount % 10; //.4
    const round = Number.parseInt((this.totalCount / 10).toFixed(0));
    return remain > 0 ? round + 1 : round;
  }

  getAccountName(account: Account): string{
    return this.accountService.getAccountName(account) || '-'
  }

  exportReport() {
    this.router.navigateByUrl(`${this.router.url}/export-report`);
  }

  repushTransactions() {
    this.router.navigateByUrl(`${this.router.url}/repush-transactions`);
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
